import { readItem, writeItem, removeItem } from './sessionStore';

/*
 * Okta answers the /authorize redirect with `error=access_denied` ("User is not
 * assigned to the client application") when the user signed in to Okta but is not
 * in the Golden Advantage app, typically because Salesforce rejected the
 * Golden Advantage group for the contact. That answer carries no user details, so
 * we remember the email while we still know it (widget sign in or SSO session) and
 * show it back on the login page together with the time, for support to look up.
 */

const ACCESS_DENIED = 'access_denied';
const LOGIN_EMAIL_KEY = 'ga-login-email';
const LOGIN_PATH_KEY = 'ga-login-path';
const ACCESS_DENIED_KEY = 'ga-login-access-denied';

/**
 * Remembers who is signing in and from which login page, the callback URL
 * tells neither.
 */
const rememberLoginAttempt = (email) => {
  if (email) {
    writeItem(LOGIN_EMAIL_KEY, email);
  }
  writeItem(LOGIN_PATH_KEY, window.location.pathname);
};

const getLoginPath = () => readItem(LOGIN_PATH_KEY);

/**
 * Reads the `access_denied` answer of an Okta redirect.
 * Returns null for any other callback.
 */
const getAccessDeniedError = (search) => {
  const params = new URLSearchParams(search || '');
  if (params.get('error') !== ACCESS_DENIED) {
    return null;
  }
  return {
    errorCode: ACCESS_DENIED,
    errorSummary: params.get('error_description') || '',
  };
};

/**
 * Stores what the login page shows for a denied sign in and returns it.
 */
const recordAccessDenied = () => {
  const details = {
    email: readItem(LOGIN_EMAIL_KEY) || '',
    occurredAt: new Date().toISOString(),
  };
  writeItem(ACCESS_DENIED_KEY, JSON.stringify(details));
  return details;
};

const getAccessDenied = () => {
  try {
    return JSON.parse(readItem(ACCESS_DENIED_KEY)) || null;
  } catch (e) {
    return null;
  }
};

const clearAccessDenied = () => removeItem(ACCESS_DENIED_KEY);

// Always US Central time, whatever the user's device is set to, so support reads
// the same clock for every user (eg. "9/28/2026, 8:50:57 AM CDT").
const formatOccurredAt = (occurredAt) =>
  new Date(occurredAt).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  });

export {
  rememberLoginAttempt,
  getLoginPath,
  getAccessDeniedError,
  recordAccessDenied,
  getAccessDenied,
  clearAccessDenied,
  formatOccurredAt,
};
